var _excluded = ["children", "id", "className", "errorText", "helpText", "warningText", "successText", "hasError", "hasBorder", "hasWarning", "label", "name", "onChange", "onBlur", "required", "hideRequired", "type", "value", "containerClass", "isInverse", "aria-label", "utils", "readOnly"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import nodeOfType from '../../utilities/types-of-node';
import Type from '../type';
import Hint from '../hint';
import Icon from '../icon';
import generateId from '../../utilities/generateID';
import InverseContext from '../../utilities/inverse-context';
import StyledFormOption from './form-option.styles';
import StyledFormOptionAsterisk from './styles/form-option.asterisk.styles';
import StyledFormOptionHintWrapper from './styles/form-option.hint-wrapper.styles';
import StyledFormOptionLabel from './styles/form-option.label.styles';
import StyledFormOptionTextLabel from './styles/form-option.label-text.styles';
import StyledFormOptionInput from './styles/form-option.label-input.styles';
import StyledFormOptionIcon from './styles/form-option.label-icon.styles';
import StyledFormOptionContainer from './styles/form-option.label-container.styles';
import withDeprecatedProps from '../../utilities/with-deprecated-props/withDeprecatedProps';
var formOptionPropTypes = {
  /** id of the component */
  id: PropTypes.string,
  /** Add custom CSS classes to component. */
  className: PropTypes.string,
  /** Error text on validation fail */
  errorText: PropTypes.node,
  /** Help text to aid user input */
  helpText: PropTypes.node,
  /** Unobtrusively styled warning text to appear below formField to aid user input. */
  warningText: PropTypes.node,
  /** Unobtrusively styled green text to appear below formField to recommend user input. */
  successText: PropTypes.node,
  /** Check for error existence  */
  hasError: PropTypes.bool,
  /** Toggles the warning error state. */
  hasWarning: PropTypes.bool,
  /** Sets the border */
  hasBorder: PropTypes.bool,
  /** component label text  */
  label: PropTypes.oneOfType([PropTypes.string, nodeOfType([Type])]),
  /** Name of the input */
  name: PropTypes.string,
  /** onChange event, when the change in component value */
  onChange: PropTypes.func,
  /** onBlur event, when the focus is removed from component */
  onBlur: PropTypes.func,
  /** Check for mandatory field */
  required: PropTypes.bool,
  /** Hide required asterisk */
  hideRequired: PropTypes.bool,
  /** Type of the component - checkbox, radio */
  type: PropTypes.string,
  /** Value of the component - Yes, No */
  value: PropTypes.string,
  /** Add custom container class to component. */
  containerClass: PropTypes.string,
  /** component children */
  children: PropTypes.node,
  /** Set to true if field is on a dark background. */
  isInverse: PropTypes.oneOf(['auto', true, false]),
  /** Aria label for button */
  'aria-label': PropTypes.string,
  /**
   * @deprecated since 2.56.0
   * @see aria-label
   */
  ariaLabel: PropTypes.string,
  /** Add utility props for styling */
  utils: PropTypes.object,
  /** Option's read only state */
  readOnly: PropTypes.bool
};
var formOptionDefaultProps = {
  id: '',
  className: '',
  errorText: '',
  helpText: '',
  warningText: '',
  successText: '',
  hasError: null,
  hasWarning: false,
  hasBorder: false,
  label: '',
  name: '',
  onChange: function onChange() {},
  onBlur: function onBlur() {},
  required: false,
  hideRequired: false,
  type: 'checkbox',
  value: '',
  containerClass: '',
  children: '',
  isInverse: 'auto',
  'aria-label': null,
  ariaLabel: null,
  utils: {},
  readOnly: false
};
var renderHint = function renderHint(errorText, hasError, hasWarning, helpText, successText, useId, warningText) {
  return /*#__PURE__*/React.createElement(StyledFormOptionHintWrapper, null, successText || errorText && hasError || warningText ? null : /*#__PURE__*/React.createElement(React.Fragment, null, helpText && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Hint, {
    id: "".concat(useId, "_hint"),
    text: helpText
  }))), hasError && /*#__PURE__*/React.createElement(Hint, {
    id: "".concat(useId, "_error"),
    text: errorText,
    type: "error"
  }), hasWarning ? /*#__PURE__*/React.createElement(Hint, {
    type: "warning",
    id: "".concat(useId, "_warning"),
    text: warningText
  }) : '', successText ? /*#__PURE__*/React.createElement(Hint, {
    type: "success",
    id: "".concat(useId, "_success"),
    text: successText
  }) : '');
};
var generateDescribedBy = function generateDescribedBy(hasError, hasWarning, successText, helpText, useId) {
  var optionStatusId = '';
  if (hasError) {
    optionStatusId = "".concat(useId, "_error");
  } else if (hasWarning) {
    optionStatusId = "".concat(useId, "_warning");
  } else if (successText !== '') {
    optionStatusId = "".concat(useId, "_success");
  }
  var hintText = helpText && "".concat(useId, "_hint");
  return optionStatusId + ' ' + hintText;
};

/**
 * @ignore @description
 * ## Form Option Component
 * Create form options using a checkbox or radio button.
 * @ignore @see Mesh: [Form Option Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/forms-formoption--default)
 *
 * @ignore @example
 * ```jsx
 * import { FormOption } from 'mesh-component-library';
 *
 * <FormOption {...props} />
 * ```
 */
export var FormOption = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var children = props.children,
    id = props.id,
    className = props.className,
    errorText = props.errorText,
    helpText = props.helpText,
    warningText = props.warningText,
    successText = props.successText,
    hasError = props.hasError,
    hasBorder = props.hasBorder,
    hasWarning = props.hasWarning,
    label = props.label,
    name = props.name,
    onChange = props.onChange,
    onBlur = props.onBlur,
    required = props.required,
    hideRequired = props.hideRequired,
    type = props.type,
    value = props.value,
    containerClass = props.containerClass,
    isInverse = props.isInverse,
    ariaLabel = props['aria-label'],
    utils = props.utils,
    readOnly = props.readOnly,
    other = _objectWithoutProperties(props, _excluded);
  var useId = id || generateId('form-option-');
  var handleOnChange = function handleOnChange(event) {
    onChange(event);
  };
  var handleOnBlur = function handleOnBlur(event) {
    onBlur(event);
  };
  var useIsInverse = React.useContext(InverseContext);
  if (isInverse !== 'auto') {
    useIsInverse = isInverse;
  }
  var generateInputId = function generateInputId(inputId) {
    return !readOnly ? inputId : "".concat(inputId, "-disabled");
  };
  var generateInputName = function generateInputName(inputName) {
    return !readOnly ? inputName : "".concat(inputName, "-disabled");
  };
  var generateHiddenInput = function generateHiddenInput(isReadOnly, defaultChecked) {
    if (isReadOnly && defaultChecked) return /*#__PURE__*/React.createElement("input", {
      type: "hidden",
      id: useId,
      name: name,
      value: value
    });
  };
  return /*#__PURE__*/React.createElement(StyledFormOption, {
    utils: utils,
    hasWarning: hasWarning,
    hasError: hasError,
    hasBorder: hasBorder,
    className: className,
    isInverse: useIsInverse,
    readOnly: readOnly
  }, /*#__PURE__*/React.createElement(StyledFormOptionLabel, {
    disabled: other.disabled,
    readOnly: readOnly,
    htmlFor: useId
  }, generateHiddenInput(readOnly, other.defaultChecked), /*#__PURE__*/React.createElement(StyledFormOptionInput, _extends({}, other, {
    ref: forwardedRef,
    id: generateInputId(useId),
    name: generateInputName(name),
    readOnly: readOnly,
    disabled: other.disabled || readOnly,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    required: required,
    type: type,
    hasWarning: hasWarning,
    hasBorder: hasBorder,
    hasError: hasError,
    value: value,
    defaultChecked: other.defaultChecked,
    "aria-describedby": generateDescribedBy(hasError, hasWarning, successText, helpText, useId),
    "aria-label": ariaLabel,
    className: "notranslate"
  })), /*#__PURE__*/React.createElement(StyledFormOptionIcon, {
    "aria-hidden": "true"
  }, (type === 'checkbox' || readOnly) && /*#__PURE__*/React.createElement(Icon, {
    icon: "checkmark",
    alt: "Checkmark"
  })), /*#__PURE__*/React.createElement(StyledFormOptionContainer, {
    className: containerClass
  }, !hideRequired && required && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(StyledFormOptionAsterisk, {
    "aria-label": "required",
    "aria-hidden": "true",
    isInverse: useIsInverse
  }, "*")), children ? /*#__PURE__*/React.createElement(StyledFormOptionTextLabel, null, children) : /*#__PURE__*/React.createElement(StyledFormOptionTextLabel, null, label))), renderHint(errorText, hasError, hasWarning, helpText, successText, useId, warningText));
});
FormOption.propTypes = formOptionPropTypes;
FormOption.defaultProps = formOptionDefaultProps;
FormOption.displayName = 'FormOption';
/**
 * @type React.FC<formOptionPropTypes>
 */
export default withDeprecatedProps(FormOption);